import { GlobalFilterKeys } from './types';

/**
 * Utility function to remove global filter keys from an object.
 * Works with any subset of GlobalFilterKeys.
 *
 * This is useful when you want to send API requests that should not include
 * global filter parameters, which are often used for client-side state management
 * but not needed by the backend.
 *
 * @param {T} obj - The object from which to strip global filter keys.
 * @returns {Omit<T, keyof GlobalFilterKeys>} A new object without the global filter keys.
 */
export const stripGlobalFilterKeys = <T extends Partial<GlobalFilterKeys>>({
  platformId,
  partnerId,
  advertiserId,
  accountId,
  ...rest
}: T): Omit<T, keyof GlobalFilterKeys> => rest;

/**
 * Serialize an object of query params into a URL-encoded string while:
 *   - skipping keys whose value is undefined, null, or an empty string
 *   - skipping empty arrays, and joining non-empty arrays with commas
 *     (so `status: ['active', 'pending']` becomes `status=active,pending`)
 *
 * Used as Axios's `paramsSerializer` so we never send "key=undefined"
 * or empty values to the backend.
 */
export const serializeQueryParams = (
  params: Record<string, unknown>
): string => {
  const entries: [string, string][] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue;
      }
      entries.push([key, value.join(',')]);
      continue;
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      entries.push([key, String(value)]);
    }
  }

  return new URLSearchParams(entries).toString();
};
